import { useEffect, useState } from 'react';
import {
  Modal, Stack, Group, Button, Select, Table, Paper, Text, Loader, Center, NumberInput, Alert, Badge, Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconAdd as IconPlus, IconChip as IconTrash, IconEndMill } from '../../components/icons/CncIcons';
import { api, imageUrl } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import type { OrderPart } from './OrderPartModal';
import type { Technician } from '../technicians/TechniciansPage';
import AddPartToQuotationModal from './AddPartToQuotationModal';
import AddProcessModal, { type ProcessForm } from './AddProcessModal';
import AddShippingModal from './AddShippingModal';
import OrderDurationSection from './OrderDurationSection';
import OrderGanttSection from './OrderGanttSection';
import CreatePdfView from './CreatePdfView';

// Shared schedule shape used by the Duration and GANTT subsections.
export interface QuotationSchedule {
  startDate: string | null;
  supplyDays: number | null;
  executionStartDate: string | null;
  executionDays: number | null;
  prepStartDate: string | null;
  prepDays: number | null;
  shippingStartDate: string | null;
  shippingDays: number | null;
}

export interface QuotationProcess {
  id: number;
  quotationPartId: number;
  entryType: 'Equipment' | 'Labour' | 'Consumable';
  equipmentId: number | null;
  technicianId: number | null;
  consumableId: number | null;
  labourDescription: string | null;
  mainQuantity: number;
  markupPct: number;
  cost: number | null;
  price: number | null;
  accessories: Array<{ id: number; accessoryId: number | null; hours: number }>;
  consumables: Array<{ id: number; consumableId: number | null; quantity: number }>;
}

export interface QuotationPart {
  id: number;
  quotationId: number;
  orderPartId: number;
  quantity: number;
  processes: QuotationProcess[];
}

export interface QuotationDetail {
  id: number;
  orderId: number;
  quotationUid: string;
  technicianId: number | null;
  technicianName: string | null;
  status: 'DRAFT' | 'SENT' | 'REFUSED' | 'ACCEPTED' | 'EXPORTED';
  sentAt: string | null;
  shippingCost: number;
  schedule: QuotationSchedule;
  pdfPath: string | null;
  pdfDesign: unknown;
  pdfExportedAt: string | null;
  totalCost: number | null;
  totalPrice: number | null;
  createdAt: string;
  parts: QuotationPart[];
}

export default function NewQuotationModal({
  orderId,
  quotationId,
  parts,
  onClose,
}: {
  orderId: number;
  quotationId: number;
  parts: OrderPart[];
  onClose: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const [detail, setDetail] = useState<QuotationDetail | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [addingPart, setAddingPart] = useState(false);
  const [addingShipping, setAddingShipping] = useState(false);
  const [addingProcessFor, setAddingProcessFor] = useState<{ qpId: number; initial: QuotationProcess | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<'edit' | 'pdf'>('edit');

  async function refresh() {
    const d = await api.get<QuotationDetail>(`/orders/${orderId}/quotations/${quotationId}`);
    setDetail(d);
  }
  useEffect(() => { refresh(); api.get<Technician[]>('/technicians').then(setTechnicians); }, []);

  async function setTechnician(techId: number | null) {
    try {
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, { technicianId: techId });
      await refresh();
    } catch (e: any) { setErr(e.message ?? 'Update failed'); }
  }

  async function setSchedule(next: QuotationSchedule) {
    try {
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, { schedule: next });
      await refresh();
    } catch (e: any) { setErr(e.message ?? 'Schedule update failed'); }
  }

  async function setStatus(status: QuotationDetail['status']) {
    try {
      const body: any = { status };
      // When transitioning into SENT the backend will auto-stamp sent_at;
      // we also pass `sentAt: now` so the UI is instantly consistent and the DateTimePicker defaults to this moment.
      if (status === 'SENT' && detail && detail.status !== 'SENT' && !detail.sentAt) {
        body.sentAt = new Date().toISOString();
      }
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, body);
      await refresh();
    } catch (e: any) { setErr(e.message ?? 'Update failed'); }
  }

  async function setSentAt(date: Date | null) {
    try {
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, { sentAt: date ? dayjs(date).toISOString() : null });
      await refresh();
    } catch (e: any) { setErr(e.message ?? 'Update failed'); }
  }

  function confirmDeleteProcess(qpId: number, procId: number) {
    modals.openConfirmModal({
      title: 'Delete process?',
      children: 'This cannot be undone.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/orders/${orderId}/quotations/${quotationId}/parts/${qpId}/processes/${procId}`);
          await refresh();
        } catch (e: any) { notifications.show({ message: e.message ?? 'Delete failed', color: 'red' }); }
      },
    });
  }

  function confirmDeletePart(qpId: number) {
    modals.openConfirmModal({
      title: 'Remove part from quotation?',
      children: 'All associated processes will be removed.',
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/orders/${orderId}/quotations/${quotationId}/parts/${qpId}`);
          await refresh();
        } catch (e: any) { notifications.show({ message: e.message ?? 'Delete failed', color: 'red' }); }
      },
    });
  }

  async function updateProcessMarkup(qpId: number, proc: QuotationProcess, markupPct: number) {
    try {
      const body: ProcessForm = {
        entryType: proc.entryType,
        equipmentId: proc.equipmentId ?? undefined,
        technicianId: proc.technicianId ?? undefined,
        consumableId: proc.consumableId ?? undefined,
        mainQuantity: proc.mainQuantity,
        markupPct,
        accessories: proc.accessories.map((a) => ({ accessoryId: a.accessoryId!, hours: a.hours })),
        consumables: proc.consumables.map((c) => ({ consumableId: c.consumableId!, quantity: c.quantity })),
      };
      await api.put(`/orders/${orderId}/quotations/${quotationId}/parts/${qpId}/processes/${proc.id}`, body);
      await refresh();
    } catch (e: any) { setErr(e.message ?? 'Update failed'); }
  }

  if (!detail) return (
    <Modal opened onClose={onClose} size="95%" title="Quotation">
      <Center p="xl"><Loader /></Center>
    </Modal>
  );

  const isExported = detail.status === 'EXPORTED';
  // Edits are blocked while EXPORTED; the user must explicitly return-to-draft.
  const locked = isExported || !writable;

  async function returnToDraft() {
    try {
      await api.put(`/orders/${orderId}/quotations/${quotationId}`, { status: 'DRAFT' });
      await refresh();
      notifications.show({ message: 'Quotation returned to DRAFT. You can now edit again.', color: 'blue' });
    } catch (e: any) {
      setErr(e.message ?? 'Failed to unlock');
    }
  }

  // Intercepts any attempt to edit an exported quotation: asks the user to go back to DRAFT.
  function gateEdit(then: () => void) {
    if (!isExported) { then(); return; }
    modals.openConfirmModal({
      title: 'Return quotation to DRAFT to edit?',
      children: <Text size="sm">This quotation is EXPORTED and locked. Return it to DRAFT to continue editing.</Text>,
      labels: { confirm: 'Return to DRAFT', cancel: 'Keep locked' },
      onConfirm: async () => { await returnToDraft(); then(); },
    });
  }

  // Enablement of the Create-PDF button:
  const sched = detail.schedule;
  const scheduleComplete =
    !!sched.startDate &&
    sched.supplyDays != null &&
    sched.executionDays != null &&
    sched.prepDays != null &&
    sched.shippingDays != null;
  const hasAtLeastOneRealPart = detail.parts.some((qp) => qp.processes.length > 0);
  const canCreatePdf = hasAtLeastOneRealPart && scheduleComplete;
  const createPdfReason = !hasAtLeastOneRealPart
    ? 'Add at least one part with a process first.'
    : !scheduleComplete ? 'Fill in the Order Duration schedule first.' : '';

  // When the user switches to the PDF view, we render the PDF panel inside the same modal.
  if (view === 'pdf') {
    return (
      <Modal
        opened
        onClose={onClose}
        size="95%"
        title={
          <Group gap="sm">
            <span className="modal-title-custom">{detail.quotationUid}</span>
            <Badge color={{ DRAFT: 'gray', SENT: 'blue', REFUSED: 'red', ACCEPTED: 'green', EXPORTED: 'violet' }[detail.status]} variant="light">
              {detail.status}
            </Badge>
          </Group>
        }
      >
        <CreatePdfView
          orderId={orderId}
          quotation={detail}
          parts={parts}
          onBack={() => setView('edit')}
          onExported={async () => { await refresh(); setView('edit'); }}
        />
      </Modal>
    );
  }

  return (
    <Modal
      opened
      onClose={onClose}
      size="95%"
      title={
        <Group gap="sm">
          <span className="modal-title-custom">{detail.quotationUid}</span>
          <Badge color={{ DRAFT: 'gray', SENT: 'blue', REFUSED: 'red', ACCEPTED: 'green', EXPORTED: 'violet' }[detail.status]} variant="light">
            {detail.status}
          </Badge>
        </Group>
      }
    >
      <Stack>
        {isExported && (
          <Alert color="violet" title="Quotation is EXPORTED — locked for editing">
            <Group justify="space-between" wrap="wrap">
              <Text size="sm">Any edit will prompt to return the quotation to DRAFT.</Text>
              <Group gap="xs">
                {detail.pdfPath && (
                  <Button size="xs" variant="light" component="a" href={`/api/v1/orders/${orderId}/quotations/${quotationId}/pdf`} target="_blank">
                    Download PDF
                  </Button>
                )}
                <Button size="xs" variant="light" color="orange" onClick={returnToDraft}>Return to DRAFT</Button>
              </Group>
            </Group>
          </Alert>
        )}
        <Group grow>
          <Select
            label="Prepared by (Technician)"
            placeholder="Select technician"
            data-tour="q-technician"
            data={technicians.map((t) => ({ value: String(t.id), label: t.name }))}
            value={detail.technicianId ? String(detail.technicianId) : null}
            onChange={(v) => setTechnician(v ? Number(v) : null)}
            clearable
            disabled={locked}
          />
          <Select
            label="Quotation Status"
            data-tour="q-status"
            data={['DRAFT', 'SENT', 'ACCEPTED', 'REFUSED', 'EXPORTED']}
            value={detail.status}
            onChange={(v) => v && setStatus(v as QuotationDetail['status'])}
            disabled={locked}
          />
          {detail.status === 'SENT' && (
            <DateTimePicker
              label="Sent at"
              value={detail.sentAt ? new Date(detail.sentAt) : null}
              onChange={setSentAt}
              valueFormat="YYYY-MM-DD HH:mm"
              clearable
              disabled={locked}
            />
          )}
        </Group>

        <div className="section">
          <div className="section-header">
            <Group gap={8} wrap="nowrap"><IconEndMill size={16} /><span>Parts Prototyping</span></Group>
          </div>
          <div className="section-body">
        <Paper withBorder>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 180 }}>Part</Table.Th>
                <Table.Th style={{ width: 80 }}>Qty</Table.Th>
                <Table.Th>Processes</Table.Th>
                <Table.Th style={{ width: 100 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detail.parts.map((qp) => {
                const part = parts.find((p) => p.id === qp.orderPartId);
                return (
                  <Table.Tr key={qp.id}>
                    <Table.Td>
                      {part?.imagePath && (
                        <img
                          src={imageUrl(part.imagePath, true)}
                          alt={part.name}
                          style={{ maxHeight: 56, display: 'block', marginBottom: 6, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}
                        />
                      )}
                      <Text fw={600}>{part?.name ?? '(deleted)'}</Text>
                      <Text size="xs" c="dimmed"><code>{part?.partUid}</code></Text>
                    </Table.Td>
                    <Table.Td>{qp.quantity}</Table.Td>
                    <Table.Td>
                      <Stack gap={6}>
                        <Text size="xs" c="dimmed">Processes apply to the <b>entire quantity</b> on this row, not per unitary part.</Text>
                        <Table>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ width: 150 }}>Type</Table.Th>
                              <Table.Th>Details</Table.Th>
                              <Table.Th style={{ width: 70 }}>Qty</Table.Th>
                              <Table.Th style={{ width: 100 }}>Markup %</Table.Th>
                              <Table.Th style={{ width: 100 }}>Cost</Table.Th>
                              <Table.Th style={{ width: 100 }}>Price</Table.Th>
                              <Table.Th style={{ width: 90 }}>Actions</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {qp.processes.map((pr) => (
                              <Table.Tr key={pr.id} style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                                <Table.Td>
                                  <Badge variant="light">{pr.entryType}</Badge>
                                </Table.Td>
                                <Table.Td>
                                  <ProcessDescription proc={pr} />
                                </Table.Td>
                                <Table.Td>{pr.mainQuantity}</Table.Td>
                                <Table.Td>
                                  <NumberInput
                                    size="xs"
                                    min={0} max={200} step={1}
                                    value={pr.markupPct}
                                    onChange={(v) => {
                                      const n = Number(v);
                                      if (!Number.isFinite(n)) return;
                                      if (n === pr.markupPct) return;
                                      updateProcessMarkup(qp.id, pr, Math.round(n));
                                    }}
                                    suffix="%"
                                    disabled={locked}
                                  />
                                </Table.Td>
                                <Table.Td>{pr.cost != null ? `€${pr.cost.toFixed(2)}` : '—'}</Table.Td>
                                <Table.Td>{pr.price != null ? `€${pr.price.toFixed(2)}` : '—'}</Table.Td>
                                <Table.Td>
                                  <Stack gap={2}>
                                    <Button size="compact-xs" variant="subtle" onClick={() => gateEdit(() => setAddingProcessFor({ qpId: qp.id, initial: pr }))}>Edit</Button>
                                    <Button size="compact-xs" variant="subtle" color="red" onClick={() => gateEdit(() => confirmDeleteProcess(qp.id, pr.id))} leftSection={<IconTrash size={10} />}>Delete</Button>
                                  </Stack>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                            <Table.Tr>
                              <Table.Td colSpan={7}>
                                <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} data-tour="q-add-process" onClick={() => gateEdit(() => setAddingProcessFor({ qpId: qp.id, initial: null }))} disabled={!writable}>
                                  Add Process
                                </Button>
                              </Table.Td>
                            </Table.Tr>
                          </Table.Tbody>
                        </Table>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Button size="compact-xs" variant="subtle" color="red" onClick={() => gateEdit(() => confirmDeletePart(qp.id))} leftSection={<IconTrash size={10} />}>Delete</Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {detail.shippingCost > 0 && (
                <Table.Tr style={{ background: 'var(--mantine-color-blue-light)' }}>
                  <Table.Td colSpan={2}>
                    <Group gap={8}>
                      <Badge color="blue" variant="filled">SHIPPING</Badge>
                      <Text fw={600}>€{detail.shippingCost.toFixed(2)}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">Added once per quotation · 0% markup</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Button size="compact-xs" variant="subtle" onClick={() => gateEdit(() => setAddingShipping(true))} disabled={!writable}>Edit</Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              )}
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} data-tour="q-add-part" onClick={() => gateEdit(() => setAddingPart(true))} disabled={!writable}>
                    Add Part to Quotation
                  </Button>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Button
                    fullWidth
                    variant="light"
                    color="blue"
                    leftSection={<IconPlus size={14} />}
                    data-tour="q-add-shipping"
                    onClick={() => gateEdit(() => setAddingShipping(true))}
                    disabled={!writable || detail.shippingCost > 0}
                  >
                    {detail.shippingCost > 0 ? 'Shipping already added — use Edit above' : 'Add Shipping to Quotation'}
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>
          </div>
        </div>

        <OrderDurationSection
          schedule={detail.schedule}
          onChange={(next) => setSchedule(next)}
          disabled={locked}
        />

        <OrderGanttSection schedule={detail.schedule} />

        <Group justify="space-between" mt="sm" align="center" wrap="wrap">
          <Group gap="md">
            <Text><b>Total cost:</b> {detail.totalCost != null ? `€${detail.totalCost.toFixed(2)}` : '—'}</Text>
            <Text><b>Total price:</b> {detail.totalPrice != null ? `€${detail.totalPrice.toFixed(2)}` : '—'}</Text>
            <Text c="dimmed">
              <b>Markup:</b>{' '}
              {(detail.totalCost != null && detail.totalPrice != null && detail.totalCost > 0)
                ? `${((detail.totalPrice / detail.totalCost - 1) * 100).toFixed(0)}%  (€${(detail.totalPrice - detail.totalCost).toFixed(2)})`
                : '—'}
            </Text>
          </Group>
          <Group gap="xs">
            <Tooltip label={canCreatePdf ? 'Open the PDF export flow' : createPdfReason} disabled={canCreatePdf}>
              <Button
                color="violet"
                variant="light"
                data-tour="q-create-pdf"
                disabled={!canCreatePdf}
                onClick={() => setView('pdf')}
              >
                Create Quotation PDF
              </Button>
            </Tooltip>
            <Button
              variant="subtle"
              color="red"
              onClick={() => {
                // Brand-new empty quotation: offer to delete; otherwise just warn that auto-save persisted edits.
                const isEmpty =
                  detail.parts.length === 0 && detail.shippingCost === 0 && detail.status === 'DRAFT';
                modals.openConfirmModal({
                  title: 'Cancel this quotation?',
                  children: isEmpty
                    ? 'This is an empty draft — it will be deleted.'
                    : 'Your edits have already been saved as you went. This just closes the window.',
                  labels: { confirm: isEmpty ? 'Delete draft' : 'Close', cancel: 'Keep editing' },
                  confirmProps: { color: 'red' },
                  onConfirm: async () => {
                    if (isEmpty) {
                      try { await api.delete(`/orders/${orderId}/quotations/${quotationId}`); } catch {}
                    }
                    onClose();
                  },
                });
              }}
            >
              Cancel
            </Button>
            <Button variant="default" onClick={onClose}>Close</Button>
            <Button
              data-tour="q-save"
              onClick={() => {
                notifications.show({ message: 'Saved.', color: 'green' });
                onClose();
              }}
            >
              Save
            </Button>
          </Group>
        </Group>
        {err && <Alert color="red">{err}</Alert>}
      </Stack>

      {addingPart && (
        <AddPartToQuotationModal
          orderId={orderId}
          quotationId={quotationId}
          parts={parts}
          existing={detail.parts}
          onClose={() => setAddingPart(false)}
          onDone={async () => { await refresh(); setAddingPart(false); }}
        />
      )}
      {addingProcessFor && (
        <AddProcessModal
          orderId={orderId}
          quotationId={quotationId}
          quotationPartId={addingProcessFor.qpId}
          initial={addingProcessFor.initial}
          onClose={() => setAddingProcessFor(null)}
          onDone={async () => { await refresh(); setAddingProcessFor(null); }}
        />
      )}
      {addingShipping && (
        <AddShippingModal
          orderId={orderId}
          quotationId={quotationId}
          initialValue={detail.shippingCost || null}
          onClose={() => setAddingShipping(false)}
          onDone={async () => { await refresh(); setAddingShipping(false); }}
        />
      )}
    </Modal>
  );
}

function ProcessDescription({ proc }: { proc: QuotationProcess }) {
  const [labels, setLabels] = useState<{ primary?: string; accessories: string[]; consumables: string[] }>({ accessories: [], consumables: [] });

  useEffect(() => {
    (async () => {
      const out: { primary?: string; accessories: string[]; consumables: string[] } = { accessories: [], consumables: [] };
      try {
        if (proc.entryType === 'Equipment' && proc.equipmentId) {
          const eq = await api.get<any>(`/equipment/${proc.equipmentId}`);
          out.primary = eq.name;
          for (const a of proc.accessories) {
            const acc = eq.accessories?.find((x: any) => x.id === a.accessoryId);
            if (acc) out.accessories.push(`${acc.name} (${a.hours}h)`);
          }
          for (const c of proc.consumables) {
            const cRow = await api.get<any>(`/consumables/${c.consumableId}`);
            if (cRow) out.consumables.push(`${cRow.name} × ${c.quantity}`);
          }
        } else if (proc.entryType === 'Labour' && proc.technicianId) {
          const t = await api.get<any>(`/technicians/${proc.technicianId}`);
          out.primary = t.name;
        } else if (proc.entryType === 'Consumable' && proc.consumableId) {
          const c = await api.get<any>(`/consumables/${proc.consumableId}`);
          out.primary = c.name;
        }
      } catch { /* ignore */ }
      setLabels(out);
    })();
  }, [proc]);

  return (
    <Stack gap={2}>
      <Text size="sm">{labels.primary ?? '(missing)'}</Text>
      {proc.entryType === 'Labour' && proc.labourDescription && (
        <Text size="xs" c="dimmed">{proc.labourDescription}</Text>
      )}
      {labels.accessories.length > 0 && <Text size="xs" c="dimmed">Accessories: {labels.accessories.join(', ')}</Text>}
      {labels.consumables.length > 0 && <Text size="xs" c="dimmed">Consumables: {labels.consumables.join(', ')}</Text>}
    </Stack>
  );
}
